import { supabase } from './supabase.js';
import { initOnboarding } from './onboarding.js';

let currentUser = null;

// Initialize authentication
export async function initAuth() {
  console.log('🔐 Checking authentication...');

  let session = null;
  try {
    const { data } = await supabase.auth.getSession();
    session = data?.session;
  } catch (e) {
    console.warn('⚠ Supabase getSession failed:', e.message);
  }

  if (session) {
    currentUser = session.user;
    console.log('✓ User authenticated:', currentUser.email);
    onAuthSuccess(currentUser);
  } else {
    console.log('⚠️ No session found, showing auth modal');
    showAuthModal();
  }

  // Listen for auth state changes
  supabase.auth.onAuthStateChange((event, session) => {
    console.log('Auth state changed:', event);
    if (session) {
      currentUser = session.user;
      onAuthSuccess(currentUser);
    }
  });
}

// Called when user is authenticated
async function onAuthSuccess(user) {
  hideAuthModal();
  updateUserDisplay(user);
  hideLoadingScreen();

  // Run onboarding wizard if not completed yet
  try {
    await initOnboarding(user);
    console.log('✓ Onboarding complete, entering world');
  } catch (err) {
    console.warn('Onboarding skipped or errored:', err);
  }

  // Dispatch event for app.js to initialize world (with updated user metadata)
  // After onboarding, user.user_metadata has been updated locally.
  // We merge Supabase data BUT always overlay local onboarding data on top,
  // since Supabase updateUser may have failed (especially for guest/anonymous users).
  let freshUser = user;
  const localMeta = user.user_metadata || {};
  try {
    const { data } = await supabase.auth.getUser();
    if (data?.user) {
      freshUser = data.user;
      // Overlay locally-set onboarding metadata on top of Supabase data
      freshUser.user_metadata = { ...freshUser.user_metadata, ...localMeta };
    }
  } catch (e) {
    console.warn('⚠ Could not refresh user from Supabase, using cached:', e.message);
  }
  // Also merge any localStorage-saved metadata (belt and suspenders)
  const saved = localStorage.getItem('veridian_user_metadata');
  if (saved) {
    try {
      freshUser.user_metadata = { ...freshUser.user_metadata, ...JSON.parse(saved) };
    } catch (_) {}
  }
  console.log('🚀 Entering world with bot:', freshUser.user_metadata?.bot_name, 'avatar:', freshUser.user_metadata?.avatar_url ? 'yes' : 'no');
  window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: freshUser }));
}

// Update user display in HUD
function updateUserDisplay(user) {
  const displayEl = document.getElementById('user-display-name');
  const signOutBtn = document.getElementById('sign-out-btn');
  
  if (displayEl) {
    displayEl.textContent = user.user_metadata?.display_name || user.email || 'User';
  }
  
  if (signOutBtn) {
    signOutBtn.style.display = 'block';
    signOutBtn.onclick = signOut;
  }
}

// Show authentication modal
export function showAuthModal() {
  // Remove existing modal if present
  const existing = document.getElementById('auth-modal');
  if (existing) existing.remove();
  
  const modal = document.createElement('div');
  modal.id = 'auth-modal';
  modal.innerHTML = `
    <div class="auth-overlay">
      <div class="auth-container">
        <h1>VERIDIAN</h1>
        <p class="subtitle">Hall of Clarity</p>
        
        <div id="auth-forms">
          <!-- Sign In Form -->
          <div id="signin-form" class="auth-form active">
            <h2>Enter the Hall</h2>
            <input type="email" id="signin-email" placeholder="Email" autocomplete="email" />
            <input type="password" id="signin-password" placeholder="Password" autocomplete="current-password" />
            <button id="signin-btn" class="btn-primary">Sign In</button>
            <p class="form-switch">
              New visitor? <a href="#" id="show-signup">Create Account</a>
            </p>
          </div>
          
          <!-- Sign Up Form -->
          <div id="signup-form" class="auth-form">
            <h2>Create Account</h2>
            <input type="text" id="signup-username" placeholder="Username" autocomplete="username" />
            <input type="email" id="signup-email" placeholder="Email" autocomplete="email" />
            <input type="password" id="signup-password" placeholder="Password" autocomplete="new-password" />
            <button id="signup-btn" class="btn-primary">Create Account</button>
            <p class="form-switch">
              Already have an account? <a href="#" id="show-signin">Sign In</a>
            </p>
          </div>
          
          <!-- Guest Mode -->
          <div class="divider">
            <span>or</span>
          </div>
          <button id="guest-btn" class="btn-secondary">Continue as Guest</button>
        </div>
        
        <div id="auth-message"></div>
      </div>
    </div>
  `;
  
  document.body.appendChild(modal);
  attachAuthHandlers();
}

function hideAuthModal() {
  const modal = document.getElementById('auth-modal');
  if (modal) {
    modal.style.opacity = '0';
    setTimeout(() => modal.remove(), 300);
  }
}

function attachAuthHandlers() {
  // Sign In
  const signinBtn = document.getElementById('signin-btn');
  const signinEmail = document.getElementById('signin-email');
  const signinPassword = document.getElementById('signin-password');
  
  signinBtn.addEventListener('click', async () => {
    const email = signinEmail.value.trim();
    const password = signinPassword.value;
    
    if (!email || !password) {
      showAuthMessage('Please enter email and password', 'error');
      return;
    }
    
    signinBtn.disabled = true;
    signinBtn.textContent = 'Signing in...';

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        showAuthMessage(error.message, 'error');
        signinBtn.disabled = false;
        signinBtn.textContent = 'Sign In';
      } else {
        showAuthMessage('Welcome back!', 'success');
        // onAuthSuccess will be called by auth state listener
      }
    } catch (err) {
      console.error('Sign-in crashed:', err);
      showAuthMessage('Sign-in failed: ' + (err.message || 'unknown error'), 'error');
      signinBtn.disabled = false;
      signinBtn.textContent = 'Sign In';
    }
  });
  
  // Sign Up
  const signupBtn = document.getElementById('signup-btn');
  const signupUsername = document.getElementById('signup-username');
  const signupEmail = document.getElementById('signup-email');
  const signupPassword = document.getElementById('signup-password');
  
  signupBtn.addEventListener('click', async () => {
    const username = signupUsername.value.trim();
    const email = signupEmail.value.trim();
    const password = signupPassword.value;
    
    if (!username || !email || !password) {
      showAuthMessage('Please fill in all fields', 'error');
      return;
    }
    
    if (password.length < 6) {
      showAuthMessage('Password must be at least 6 characters', 'error');
      return;
    }
    
    signupBtn.disabled = true;
    signupBtn.textContent = 'Creating account...';
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          display_name: username,
          avatar_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}`
        }
      }
    });
    
    if (error) {
      showAuthMessage(error.message, 'error');
      signupBtn.disabled = false;
      signupBtn.textContent = 'Create Account';
    } else if (data?.user && !data?.session) {
      // Email confirmation required — try auto-signin
      const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
      if (signInErr) {
        // If sign-in fails, enter demo mode with the username they chose
        console.warn('Auto sign-in after signup failed, entering demo mode');
        showAuthMessage('Welcome! Entering Veridian...', 'success');
        const demoUser = {
          id: `signup-${Date.now()}`,
          email: email,
          user_metadata: { username, display_name: username, is_guest: false }
        };
        currentUser = demoUser;
        hideAuthModal();
        updateUserDisplay(demoUser);
        hideLoadingScreen();
        import('./onboarding.js').then(({ initOnboarding }) => {
          initOnboarding(demoUser).then(() => {
            const saved = localStorage.getItem('veridian_user_metadata');
            if (saved) { try { demoUser.user_metadata = { ...demoUser.user_metadata, ...JSON.parse(saved) }; } catch(_){} }
            window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: demoUser }));
          }).catch(() => {
            window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: demoUser }));
          });
        });
      }
      // If signIn succeeded, onAuthSuccess fires via the state listener
    } else {
      showAuthMessage('Account created! Welcome to Veridian.', 'success');
      // onAuthSuccess will be called by auth state listener
    }
  });
  
  // Guest Mode (uses Supabase anonymous auth, falls back to local demo)
  const guestBtn = document.getElementById('guest-btn');
  guestBtn.addEventListener('click', async () => {
    guestBtn.disabled = true;
    guestBtn.textContent = 'Entering as guest...';

    try {
      const { data, error } = await supabase.auth.signInAnonymously({
        options: {
          data: {
            username: `Guest${Date.now().toString().slice(-6)}`,
            display_name: 'Guest',
            is_guest: true
          }
        }
      });

      if (error) throw error;
      showAuthMessage('Entering Veridian...', 'success');
      // onAuthSuccess will be called by auth state listener
    } catch (err) {
      console.warn('Supabase guest auth failed, entering demo mode:', err.message);
      // Fall back to local demo mode — no Supabase needed
      enterDemoMode();
    }
  });
  
  // Form switching
  document.getElementById('show-signup').addEventListener('click', (e) => {
    e.preventDefault();
    document.getElementById('signin-form').classList.remove('active');
    document.getElementById('signup-form').classList.add('active');
  });
  
  document.getElementById('show-signin').addEventListener('click', (e) => {
    e.preventDefault();
    document.getElementById('signup-form').classList.remove('active');
    document.getElementById('signin-form').classList.add('active');
  });
  
  // Enter key support
  signinPassword.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') signinBtn.click();
  });
  
  signupPassword.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') signupBtn.click();
  });
}

function showAuthMessage(message, type) {
  const msgEl = document.getElementById('auth-message');
  msgEl.textContent = message;
  msgEl.className = `auth-message ${type}`;
  msgEl.style.display = 'block';
}

// Demo mode — bypasses Supabase entirely, creates a local-only user
function enterDemoMode() {
  const guestId = `Guest${Date.now().toString().slice(-6)}`;
  const demoUser = {
    id: `demo-${Date.now()}`,
    email: '',
    user_metadata: {
      username: guestId,
      display_name: guestId,
      is_guest: true,
      is_demo: true,
    }
  };
  currentUser = demoUser;
  showAuthMessage('Entering Veridian...', 'success');
  hideAuthModal();
  updateUserDisplay(demoUser);
  hideLoadingScreen();

  // Run onboarding for demo user
  import('./onboarding.js').then(({ initOnboarding }) => {
    initOnboarding(demoUser).then(() => {
      // Merge localStorage metadata (onboarding saves here when Supabase fails)
      const saved = localStorage.getItem('veridian_user_metadata');
      if (saved) {
        try { demoUser.user_metadata = { ...demoUser.user_metadata, ...JSON.parse(saved) }; } catch (_) {}
      }
      console.log('🚀 Demo user entering world with bot:', demoUser.user_metadata?.bot_name, 'avatar:', demoUser.user_metadata?.avatar_url ? 'yes' : 'no');
      window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: demoUser }));
    }).catch((err) => {
      console.warn('Onboarding error, entering world anyway:', err);
      window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: demoUser }));
    });
  });
}

// Sign out
export async function signOut() {
  try { await supabase.auth.signOut(); } catch (_) {}
  currentUser = null;
  localStorage.removeItem('veridian_user_metadata');
  window.location.reload();
}

// Get current user
export function getCurrentUser() {
  return currentUser;
}

function hideLoadingScreen() {
  const loadingScreen = document.getElementById('loading-screen');
  if (loadingScreen) {
    loadingScreen.classList.add('hidden');
    setTimeout(() => loadingScreen.remove(), 1000);
  }
}
